// src/ui/BillingAdmin.tsx
import { useEffect, useState } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { supabase } from "../lib/supabase";
import { Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";

type Invoice = {
  id: string;
  date: string;
  amount: number;
  url: string;
};

export default function BillingAdmin() {
  const [contractUrl, setContractUrl] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [consumption, setConsumption] = useState<{ month: string; messages: number }[]>([]);
  const [estimate, setEstimate] = useState<number>(0);
  const [consumed, setConsumed] = useState<number>(0);
  const [included, setIncluded] = useState<number>(0);
  const [overageFee, setOverageFee] = useState<number>(0);
  const [messageBreakdown, setMessageBreakdown] = useState({
    received: 0,
    sentAssistant: 0,
    sentPractitioner: 0,
  });
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf("month"));
  const [monthMode, setMonthMode] = useState<"current" | "previous" | "custom">("current");  
  const [templateMessages, setTemplateMessages] = useState(0);
  const [templateCost, setTemplateCost] = useState(0);

  const monthOptions = Array.from({ length: 12 }, (_, index) =>
    dayjs().subtract(index, "month").startOf("month")
  );

  useEffect(() => {
    loadContract();
    loadInvoices();
  }, []);

  useEffect(() => {
    if (monthMode === "current") {
      setSelectedMonth(dayjs().startOf("month"));
    }
    if (monthMode === "previous") {
      setSelectedMonth(dayjs().subtract(1, "month").startOf("month"));
    }
  }, [monthMode]);

  useEffect(() => {
    loadConsumption(selectedMonth);
    loadDetailedConsumption(selectedMonth);
    loadTemplateStats(selectedMonth);
  }, [selectedMonth]);

  
  async function loadContract() {
  const { data, error } = await supabase
    .from("clients")
    .select("contract_url")
    .single();

  if (!error && data?.contract_url) {
    // Ajoute un paramètre unique pour casser le cache
    const bust = `?t=${Date.now()}`;
    setContractUrl(data.contract_url + bust);
  }
}

  async function loadDetailedConsumption(month = selectedMonth) {
    const start = month.startOf("month").toISOString();
    const end = month.endOf("month").toISOString();
  
    // 1️⃣ Messages reçus (patient → assistant)
    const { count: received } = await supabase
      .from("conversations")
      .select("id", { count: "exact", head: true })
      .gte("created_at", start)
      .lte("created_at", end)
      .eq("role", "user");
  
    // 2️⃣ Messages envoyés par assistant
    const { count: sentAssistant } = await supabase
      .from("conversations")
      .select("id", { count: "exact", head: true })
      .gte("created_at", start)
      .lte("created_at", end)
      .eq("role", "assistant");
  
    // 3️⃣ Messages envoyés par praticien
    const { count: sentPractitioner } = await supabase
      .from("conversations")
      .select("id", { count: "exact", head: true })
      .gte("created_at", start)
      .lte("created_at", end)
      .eq("role", "human");
  
    setMessageBreakdown({
      received: received || 0,
      sentAssistant: sentAssistant || 0,
      sentPractitioner: sentPractitioner || 0,
    });
  }

  async function loadTemplateStats(month = selectedMonth) {
    const start = month.startOf("month").toISOString();
    const end = month.endOf("month").toISOString();
  
    // Nombre de messages template
    const { count: templateCount } = await supabase
      .from("conversations")
      .select("id", { count: "exact", head: true })
      .gte("created_at", start)
      .lte("created_at", end)
      .eq("is_template", true);
  
    setTemplateMessages(templateCount || 0);
  
    // Récupération du prix template client
    const { data: client } = await supabase
      .from("clients")
      .select("template_message_fee_sgd")
      .single();
  
    if (client) {
      setTemplateCost((templateCount || 0) * client.template_message_fee_sgd);
    }
}

  async function loadInvoices() {
    const { data, error } = await supabase
      .from("invoices")
      .select("id, date, amount, url, status")
      .order("date", { ascending: false });
    if (!error && data) setInvoices(data);
  }

  async function loadConsumption(month = selectedMonth) {
    // Récupère infos forfait client
    const { data: client } = await supabase
      .from("clients")
      .select("included_messages, additional_message_fee_sgd")
      .single();
  
    if (client) {
      setIncluded(client.included_messages || 0);
      setOverageFee(client.additional_message_fee_sgd || 0);
    }
  
    // Récupère le nombre de messages consommés ce mois-ci
    const start = month.startOf("month").toISOString();
    const end = month.endOf("month").toISOString();
  
    const { count } = await supabase
      .from("conversations")
      .select("id", { count: "exact", head: true })
      .gte("created_at", start)
      .lte("created_at", end);
  
    setConsumed(count || 0);
  
    // Calcul estimé
    if (client) {
      const consumedMessages = count || 0;
      const extraMessages = Math.max(consumedMessages - client.included_messages, 0);
      const billedBundles = Math.ceil(extraMessages / 1000);
      setEstimate(billedBundles * client.additional_message_fee_sgd);
    }
  }

  const grouped = invoices.reduce((acc: Record<string, Invoice[]>, inv) => {
    const label = dayjs(inv.date).format("MMMM YYYY");
    if (!acc[label]) acc[label] = [];
    acc[label].push(inv);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Billing & Admin</h1>

      <Tabs.Root defaultValue="invoices">
        <Tabs.List className="flex border-b mb-6">
          <Tabs.Trigger
            value="invoices"
            className="px-4 py-2 text-sm font-medium text-gray-600 data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            Invoices
          </Tabs.Trigger>
          <Tabs.Trigger
            value="consumption"
            className="px-4 py-2 text-sm font-medium text-gray-600 data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            Consumption
          </Tabs.Trigger>
          <Tabs.Trigger
            value="contract"
            className="px-4 py-2 text-sm font-medium text-gray-600 data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            Contract
          </Tabs.Trigger>
        </Tabs.List>

        {/* Invoices */}
        <Tabs.Content value="invoices">
          {Object.keys(grouped).length > 0 ? (
            <div className="space-y-8">
              {Object.entries(grouped).map(([month, items]) => (
                <div key={month}>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{month}</h3>
                  <table className="min-w-full text-sm border">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Amount</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((inv) => {
                        const isViewed = localStorage.getItem(`invoice_viewed_${inv.id}`);
                        return (
                          <tr key={inv.id} className="border-t">
                            <td className="px-4 py-2">{dayjs(inv.date).format("DD MMM YYYY")}</td>
                            <td className="px-4 py-2">{inv.amount.toFixed(2)} SGD</td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${
                                  inv.status === "paid"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }`}
                              >
                                {inv.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <a
                                href={inv.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-blue-600 hover:underline"
                                onClick={() => localStorage.setItem(`invoice_viewed_${inv.id}`, "true")}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                                {!isViewed && (
                                  <span className="ml-2 px-1 text-xs text-white bg-red-500 rounded">
                                    NEW
                                  </span>
                                )}
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No invoices available.</p>
          )}
        </Tabs.Content>

        {/* Consumption */}
        <Tabs.Content value="consumption">
          <div className="flex flex-col gap-3 mb-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                Consumption — {selectedMonth.format("MMMM YYYY")}
              </h2>
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                <button
                  type="button"
                  onClick={() => setMonthMode("current")}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                    monthMode === "current"
                      ? "bg-white text-primary shadow"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  This month
                </button>
                <button
                  type="button"
                  onClick={() => setMonthMode("previous")}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                    monthMode === "previous"
                      ? "bg-white text-primary shadow"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  Last month
                </button>
              </div>
            </div>
            <div className="flex">
              <select
                value={selectedMonth.format("YYYY-MM")}
                onChange={(event) => {
                  const nextMonth = dayjs(`${event.target.value}-01`).startOf("month");
                  setSelectedMonth(nextMonth);
                  setMonthMode("custom");
                }}
                className="w-full sm:w-auto rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {monthOptions.map((monthOption) => (
                  <option key={monthOption.format("YYYY-MM")} value={monthOption.format("YYYY-MM")}>
                    {monthOption.format("MMMM YYYY")}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bar gauge existing */}
          <h3 className="font-semibold text-md mb-2 text-gray-700">Plan usage</h3>
          <div className="text-lg font-medium mb-4">
            {consumed} / {included} messages included
          </div>
        
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
            <div
              className={`h-2.5 rounded-full ${consumed <= included ? "bg-green-400" : "bg-red-400"}`}
              style={{ width: `${Math.min((consumed / included) * 100, 100)}%` }}
            ></div>
          </div>
        
          <div className="mt-4 text-gray-700">
            Estimated additional charge next invoice:{" "}
            <span className="font-semibold text-primary">{estimate.toFixed(2)} SGD</span>
          </div>

          <h2 className="text-xl font-bold mb-4 text-gray-800">Consumption</h2>
        
          {/* Top summary — FULL WIDTH HEADER */}
          <div className="w-full mb-6">
            <div className="p-6 bg-gray-50 rounded-lg shadow text-center w-full">
              <div className="text-4xl font-bold">{consumed}</div>
              <div className="text-sm text-gray-600">Total messages</div>
            </div>
          </div>
          
          {/* 3 detailed cards under the header */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          
            <div className="p-4 bg-gray-50 rounded-lg shadow text-center">
              <div className="text-2xl font-bold">{messageBreakdown.received}</div>
              <div className="text-sm text-gray-600">📥 Received</div>
            </div>
          
            <div className="p-4 bg-gray-50 rounded-lg shadow text-center">
              <div className="text-2xl font-bold">{messageBreakdown.sentAssistant}</div>
              <div className="text-sm text-gray-600">🤖 Sent by assistant</div>
            </div>
          
            <div className="p-4 bg-gray-50 rounded-lg shadow text-center">
              <div className="text-2xl font-bold">{messageBreakdown.sentPractitioner}</div>
              <div className="text-sm text-gray-600">👩‍⚕️ Sent by practitioner</div>
            </div>
          
          </div>
        
          {/* Templates */}
          <h3 className="font-semibold text-md mb-2 text-gray-700">WhatsApp Template Messages</h3>
          <ul className="mb-6 text-gray-700 space-y-1">
            <li><b>{templateMessages}</b> template messages sent</li>
            <li>Estimated additional charge next invoice: <b>{templateCost.toFixed(2)} SGD</b></li>
          </ul>
        </Tabs.Content>
        
        {/* Contract */}
        <Tabs.Content value="contract">
          {contractUrl ? (
            <iframe
              src={contractUrl}
              className="w-full h-[600px] border rounded"
              title="Signed Contract"
            />
          ) : (
            <p className="text-gray-500">No contract available.</p>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
